<template>
  <n-layout>
    <n-layout-header position="static">
      <app-header :menu-options="menuOptions"/>
      <page-header :menu-options="menuOptions" :isDetailPage="!routeInMenu" />
    </n-layout-header>
    <n-layout-content>
      <slot/>
    </n-layout-content>
  </n-layout>
</template>

<script lang="ts" setup>
import {NLayout, NLayoutContent, NLayoutHeader} from "naive-ui";
import AppHeader from "@/layout/AppHeader.vue";
import PageHeader from "@/layout/PageHeader.vue";
import {useI18n} from "vue-i18n";
import {useAuthStore} from "@/stores/authentication-store";
import type {MenuOption} from "@/models/menu/menu-option";
import { useRoute } from 'vue-router';
import { computed } from 'vue';
import { useAssignmentStore } from "@/stores/assignment/assignment-store";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { FeatureToggle } from "@/constants/feature-toggle";
import { Role } from "@/constants/role";

const {t} = useI18n();

const authStore = useAuthStore();
const organizationStore = useOrganizationStore();
const route = useRoute();
const assignmentStore = useAssignmentStore();

const menuOptions = computed(() => [
  {
    to: "/relation/list",
    label: t('page_header.relation.title'),
    key: "relation",
    show: authStore.hasClaims(["relation.read-list"])
  },
  {
    to: "/trajectory/list",
    label: t('page_header.trajectory.title'),
    key: "trajectory",
    show: authStore.hasClaims(["trajectory.read"]),
    subMenus: [
      {
        to: "/trajectory/list/plan",
        label: t('page_header.trajectory.sub_pages.plan'),
        key: "trajectory-list-plan",
        show: authStore.hasClaims(["trajectory.read"]),
      },
      {
        to: "/trajectory/list/planned",
        label: t('page_header.trajectory.sub_pages.planned'),
        key: "trajectory-list-planned",
        show: authStore.hasClaims(["trajectory.read"]),
      },
      {
        to: "/trajectory/list/transit",
        label: t('page_header.trajectory.sub_pages.transit'),
        key: "trajectory-list-transit",
        show: authStore.hasClaims(["trajectory.read"]),
      },
      {
        to: "/trajectory/list/completed",
        label: t('page_header.trajectory.sub_pages.completed'),
        key: "trajectory-list-completed",
        show: authStore.hasClaims(["trajectory.read"]),
      },
      {
        to: "/trajectory/list/outsourced",
        label: t('page_header.trajectory.sub_pages.outsourced'),
        key: "trajectory-list-outsourced",
        show: authStore.hasClaims(["trajectory.read"]),
      }
    ]
  },
  {
    to: "/assignment",
    label: t('page_header.assignment.title'),
    key: "assignment",
    show: authStore.hasAtLeastOneClaim(["assignment.read", 'assignment.read-own-assignment']),
    subMenus: [
      {
        to: "/assignment/list/plan",
        label: t('page_header.assignment.sub_pages.plan'),
        key: "assignment-list-plan",
        show: authStore.hasAtLeastOneClaim(["assignment.read", 'assignment.read-own-assignment']),
      },
      {
        to: "/assignment/list/planned",
        label: t('page_header.assignment.sub_pages.planned'),
        key: "assignment-list-planned",
        show: authStore.hasAtLeastOneClaim(["assignment.read", 'assignment.read-own-assignment']),
      },
      {
        to: "/assignment/list/transit",
        label: t('page_header.assignment.sub_pages.transit'),
        key: "assignment-list-transit",
        show: authStore.hasAtLeastOneClaim(["assignment.read", 'assignment.read-own-assignment']),
      },
      {
        to: "/assignment/list/intermediate-location",
        label: t('page_header.assignment.sub_pages.intermediate_location'),
        key: "assignment-list-intermediate-location",
        show: authStore.hasAtLeastOneClaim(["assignment.read", 'assignment.read-own-assignment']),
      },
      {
        to: "/assignment/list/ready-for-promatie",
        label: t('page_header.assignment.sub_pages.ready_for_promatie', { amount: assignmentStore.totalAmountReadyForPromatieAssignments >= 10 ? '(9+)' : `(${assignmentStore.totalAmountReadyForPromatieAssignments})` }),
        key: "assignment-list-ready-for-promatie",
        show: authStore.hasAtLeastOneClaim(["assignment.read", 'assignment.read-own-assignment'])
            && authStore.role !== Role.Client
            && authStore.role !== Role.ExternalUser,
      },
      {
        to: "/assignment/list/completed",
        label: t('page_header.assignment.sub_pages.completed'),
        key: "assignment-list-completed",
        show: authStore.hasAtLeastOneClaim(["assignment.read", 'assignment.read-own-assignment']),
      },
      {
        to: "/assignment/list/received",
        label: t('page_header.assignment.sub_pages.received', { amount: assignmentStore.totalAmountReceivedAssignments >= 10 ? '(9+)' : `(${assignmentStore.totalAmountReceivedAssignments})` }),
        key: "assignment-list-received",
        show: authStore.hasAtLeastOneClaim(["assignment.read", 'assignment.read-own-assignment'])
            && authStore.role !== Role.Client
            && authStore.role !== Role.ExternalUser,
      },
      {
        to: "/assignment/list/all",
        label: t('page_header.assignment.sub_pages.all'),
        key: "assignment-list-all",
        show: authStore.hasAtLeastOneClaim(["assignment.read", 'assignment.read-own-assignment']),
      }
    ]
  },
  {
    to: "/vehicle",
    label: t('page_header.vehicle.title'),
    key: "vehicle",
    show: authStore.hasClaims(["vehicle.read"]),
    subMenus: [
      {
        to: "/vehicle/list/all",
        label: t('page_header.vehicle.sub_pages.all'),
        key: "vehicle-list-all",
        show: authStore.hasClaims(["vehicle.read"]),
      },
      {
        to: "/vehicle/list/intermediate-location",
        label: t('page_header.vehicle.sub_pages.intermediate_location'),
        key: "vehicle-list-intermediate-location",
        show: authStore.hasClaims(["vehicle.read"]),
      },
      {
        to: "/vehicle/list/in-storage",
        label: t('page_header.vehicle.sub_pages.in_storage'),
        key: "vehicle-list-in-storage",
        show: authStore.hasClaims(["vehicle.read"]),
      }
    ]
  },
  {
    to: "/transport",
    label: t('page_header.transport.title'),
    key: "transport",
    show: authStore.hasClaims(["transport.read"]),
    subMenus: [
      {
        to: "/transport/list/day-planner",
        label: t('page_header.transport.sub_pages.dayplanner'),
        key: "transport-day-planner",
        show: authStore.hasClaims(["transport.read"]),
      },
      {
        to: "/transport/list/plan",
        label: t('page_header.transport.sub_pages.plan'),
        key: "transport-list-plan",
        show: authStore.hasClaims(["transport.read"]),
      },
      {
        to: "/transport/list/planned",
        label: t('page_header.transport.sub_pages.planned'),
        key: "transport-list-planned",
        show: authStore.hasClaims(["transport.read"]),
      },
      {
        to: "/transport/list/transit",
        label: t('page_header.transport.sub_pages.transit'),
        key: "transport-list-transit",
        show: authStore.hasClaims(["transport.read"]),
      },
      {
        to: "/transport/list/completed",
        label: t('page_header.transport.sub_pages.completed'),
        key: "transport-list-completed",
        show: authStore.hasClaims(["transport.read"]),
      }
    ]
  },
  {
    to: "/transport-vehicle/list",
    label: t('page_header.transport_vehicle.title'),
    key: "transport-vehicle",
    show: authStore.hasClaims(["transport-vehicle.read"])
  },
  {
    to: "/organization",
    label: t('page_header.organization.title'),
    key: "organization",
    show: authStore.hasClaims(["organization-settings.update"]),
    subMenus: [
      {
        to: "/organization/users",
        label: t('page_header.organization.sub_pages.users'),
        key: "organization-users",
        show: authStore.hasClaims(["organization-users.read"]),
      },
      {
        to: "/organization/settings",
        label: t('page_header.organization.sub_pages.settings'),
        key: "organization-settings",
        show: authStore.hasClaims(["organization-settings.read"]),
      },
      {
        to: "/organization/transport-reasons",
        label: t('page_header.organization.sub_pages.transport_reasons'),
        key: "organization-transport-reasons",
        show: authStore.hasClaims(["organization-settings.read"]),
      },
    ]
  },
  {
    to: "/management",
    label: t('page_header.management.title'),
    key: "management",
    show: authStore.hasClaims(["management-general.read"]) && organizationStore.hasFeature(FeatureToggle.TARIFFS),
    subMenus: [
      {
        to: "/management/rate-groups-sales",
        label: t('page_header.management.sub_pages.rate_groups_sales'),
        key: "management-rate-groups-sales",
        show: authStore.hasClaims(["management-general.read"]),
      },
      {
        to: "/management/rate-groups-purchasing",
        label: t('page_header.management.sub_pages.rate_groups_purchasing'),
        key: "management-rate-groups-purchasing",
        show: authStore.hasClaims(["management-general.read"]),
      }
    ]
  },
  {
    to: "/administration/organizations",
    label: t('page_header.administration.title'),
    key: "administration",
    show: authStore.hasClaims(["administration-organizations.read"]),
  },
]);

const isCurrentRouteInMenuOptions = (options: MenuOption[], currentRoute: string): boolean => {
  return options.some(option => {
    if (option.to === currentRoute) {
      return true;
    }
    if (option.subMenus) {
      return isCurrentRouteInMenuOptions(option.subMenus, currentRoute);
    }
    return false;
  });
};

const routeInMenu = computed(() => {
  const currentRoute = route.path;

  return isCurrentRouteInMenuOptions(menuOptions.value, currentRoute);
})
</script>

<style lang="scss"></style>
